import type { RecipeFamily } from "../../domain/types";
import { option, pantry, slot } from "./helpers";

export const grainBowlRecipes: RecipeFamily[] = [
  {
    id: "lemon-quinoa-chickpea-bowl",
    nameTemplate: "Lemon {base.name} & {protein.name} bowl",
    translations: {
      tr: { nameTemplate: "Limonlu {base.name} ve {protein.name} kasesi" },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 15,
    slots: [
      slot("base", "Grain", true, "quinoa", [
        option("quinoa", 300, "g", "cooked and cooled", 3),
        option("rice", 300, "g", "cooked and cooled", 2),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("lentils", 260, "g", "cooked and drained", 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "cucumber", [
        option("cucumber", 250, "g", "diced", 3),
        option("tomato", 250, "g", "diced", 2),
      ]),
      slot("richness", "Richness", true, "avocado", [
        option("avocado", 1, "piece", "sliced", 3),
        option("feta", 100, "g", "crumbled", 1),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("oil", "Dressing fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp", undefined, 3),
        option("tahini", 2, "tbsp", undefined, 2),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 20, "g", "chopped", 3),
        option("mint", 15, "g", "chopped", 2),
        option("cilantro", 20, "g", "chopped", 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {acid.name}, {oil.name}, {salt.name}, and {black-pepper.name} in a large bowl.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede {acid.name}, {oil.name}, {salt.name} ve {black-pepper.name} çırpın.",
          },
        },
      },
      {
        id: "season-base",
        template:
          "Add {base.name}, {protein.name}, and {vegetable.name}. Toss until evenly dressed.",
        translations: {
          tr: {
            template:
              "{base.name}, {protein.name} ve {vegetable.name} ekleyin. Sos eşit dağılana kadar karıştırın.",
          },
        },
      },
      {
        id: "build-bowls",
        template:
          "Divide between bowls and top with {richness.name} and {herb.name} if using.",
        translations: {
          tr: {
            template:
              "Kaselere paylaştırın ve üzerine {richness.name} ile kullanıyorsanız {herb.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "fresh"],
    editorialPriority: 10,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "peanut-avocado-rice-bowl",
    nameTemplate: "{base.name} bowl with {protein.name} & {sauce.name}",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {sauce.name} soslu {base.name} kasesi",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("base", "Grain", true, "rice", [
        option("rice", 300, "g", "cooked and cooled", 3),
        option("quinoa", 300, "g", "cooked and cooled", 2),
      ]),
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 240, "g", "drained", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("richness", "Creamy element", true, "avocado", [
        option("avocado", 1, "piece", "sliced", 3),
        option("tahini", 2, "tbsp", undefined, 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "carrot", [
        option("carrot", 180, "g", "coarsely grated", 3),
        option("cucumber", 200, "g", "diced", 2),
        option("corn", 200, "g", "drained", 2),
        option("celery", 150, "g", "thinly sliced", 1),
        option("cabbage", 150, "g", "finely shredded", 1),
      ]),
      slot("sauce", "Dressing", true, "peanut-butter", [
        option("peanut-butter", 3, "tbsp", undefined, 3),
        option("tahini", 3, "tbsp", undefined, 2),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch"),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "chopped", 3),
        option("parsley", 15, "g", "chopped", 2),
      ]),
      pantry.water(40),
      pantry.salt,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {sauce.name}, {acid.name}, {water.name}, and {salt.name} into a smooth, pourable dressing. Add {heat.name} if using.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {acid.name}, {water.name} ve {salt.name} pürüzsüz ve akışkan bir sos olana kadar çırpın. Kullanıyorsanız {heat.name} ekleyin.",
          },
        },
      },
      {
        id: "build-bowls",
        template:
          "Divide {base.name} between two bowls. Arrange {protein.name}, {richness.name}, and {vegetable.name} on top.",
        translations: {
          tr: {
            template:
              "{base.name} iki kaseye paylaştırın. Üzerine {protein.name}, {richness.name} ve {vegetable.name} dizin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Spoon the dressing over the bowls and scatter {herb.name} if using.",
        translations: {
          tr: {
            template:
              "Sosu kaselerin üzerine gezdirin ve kullanıyorsanız {herb.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "miso-rice-tofu-bowl",
    nameTemplate: "Miso {protein.name} & {vegetable.name} rice bowl",
    translations: {
      tr: {
        nameTemplate: "Misolu {protein.name} ve {vegetable.name} pirinç kasesi",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 18,
    totalMinutes: 25,
    slots: [
      slot("base", "Grain", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained and dried", 1),
      ]),
      slot("vegetable", "Green vegetable", true, "broccoli", [
        option("broccoli", 300, "g", "cut into small florets", 3),
        option("green-beans", 300, "g", "trimmed and halved", 2),
      ]),
      slot("fresh", "Fresh vegetable", true, "carrot", [
        option("carrot", 180, "g", "coarsely grated", 3),
        option("cabbage", 180, "g", "thinly sliced", 2),
      ]),
      slot("sauce", "Dressing", true, "miso", [
        option("miso", 1, "tbsp", undefined, 3),
        option("soy-sauce", 2, "tbsp", undefined, 2),
      ]),
      slot("ginger", "Aromatic", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp", undefined, 3),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      pantry.water(45),
    ],
    steps: [
      {
        id: "mix-dressing",
        template:
          "Mix {sauce.name}, {ginger.name}, and {water.name} into a pourable dressing.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {ginger.name} ve {water.name} karıştırarak akışkan bir sos hazırlayın.",
          },
        },
      },
      {
        id: "cook-topping",
        template:
          "Heat {oil.name} in a wide pan. Brown {protein.name} for 5 minutes, then add {vegetable.name} and cook until tender-crisp.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} ısıtın. {protein.name} 5 dakika kızartın, ardından {vegetable.name} ekleyip gevrek-yumuşak olana kadar pişirin.",
          },
        },
      },
      {
        id: "build-bowls",
        template:
          "Divide {base.name} between bowls. Add the cooked topping and {fresh.name}, then spoon over the dressing.",
        translations: {
          tr: {
            template:
              "{base.name} kaselere paylaştırın. Pişmiş malzemeleri ve {fresh.name} ekleyin, ardından sosu üzerine gezdirin.",
          },
        },
      },
      {
        id: "finish",
        template: "Add {finish.name} if using and serve.",
        translations: {
          tr: {
            template: "Kullanıyorsanız {finish.name} ekleyin ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "umami", "fresh", "crunch"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "garlic-tahini-quinoa-bowl",
    nameTemplate: "{base.name} bowl with {protein.name} & {sauce.name}",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {sauce.name} soslu {base.name} kasesi",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("base", "Grain", true, "quinoa", [
        option("quinoa", 300, "g", "cooked", 3),
        option("rice", 300, "g", "cooked", 2),
      ]),
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 240, "g", "drained", 3),
        option("lentils", 260, "g", "cooked and drained", 2),
        option("chickpeas", 240, "g", "drained", 1),
      ]),
      slot("veg", "Cooked vegetable", true, "spinach", [
        option("spinach", 120, "g", "roughly chopped", 3),
        option("mushroom", 200, "g", "sliced", 2),
        option("broccoli", 200, "g", "cut into small florets", 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("sauce", "Dressing", true, "tahini", [
        option("tahini", 3, "tbsp", undefined, 3),
        option("peanut-butter", 3, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 2, "tbsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp", undefined, 3),
        option("chili-flakes", 1, "pinch", undefined, 2),
      ]),
      pantry.water(40),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {sauce.name}, {acid.name}, {water.name}, {salt.name}, and {black-pepper.name} into a smooth, pourable dressing.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {acid.name}, {water.name}, {salt.name} ve {black-pepper.name} pürüzsüz ve akışkan bir sos olana kadar çırpın.",
          },
        },
      },
      {
        id: "cook-veg",
        template:
          "Warm {oil.name} in a frying pan over medium heat. Add {garlic.name} and cook for 30 seconds, then add {veg.name} and stir for 3 to 4 minutes until tender.",
        translations: {
          tr: {
            template:
              "Orta ateşte kızartma tavasında {oil.name} ısıtın. {garlic.name} ekleyip 30 saniye pişirin, ardından {veg.name} ekleyip yumuşayana kadar 3-4 dakika karıştırın.",
          },
        },
      },
      {
        id: "warm-protein",
        template:
          "Add {protein.name} to the pan and toss for 2 minutes until warmed through.",
        translations: {
          tr: {
            template:
              "Tavaya {protein.name} ekleyin ve ısınana kadar 2 dakika karıştırın.",
          },
        },
      },
      {
        id: "build-bowls",
        template:
          "Divide {base.name} between bowls. Top with the protein and vegetable mixture. Spoon over the dressing and add {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} kaselere paylaştırın. Üzerine protein ve sebze karışımını koyun. Sosu gezdirin ve kullanıyorsanız {finish.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "umami"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "roasted-sweet-potato-lentil-bowl",
    nameTemplate: "Roasted {vegetable.name} & {protein.name} grain bowl",
    translations: {
      tr: {
        nameTemplate:
          "Fırınlanmış {vegetable.name} ve {protein.name} tahıl kasesi",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 38,
    slots: [
      slot("base", "Grain", true, "quinoa", [
        option("quinoa", 300, "g", "cooked", 3),
        option("rice", 300, "g", "cooked", 2),
      ]),
      slot("vegetable", "Roasting vegetable", true, "sweet-potato", [
        option("sweet-potato", 400, "g", "cut into 2 cm cubes", 3),
        option("cauliflower", 400, "g", "cut into florets", 2),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 260, "g", "cooked and drained", 3),
        option("chickpeas", 240, "g", "drained and dried", 2),
      ]),
      slot("green", "Fresh green", true, "spinach", [
        option("spinach", 120, "g", "roughly chopped"),
      ]),
      slot("dressing", "Creamy dressing", true, "tahini", [
        option("tahini", 2, "tbsp", undefined, 3),
        option("yogurt", 120, "g", undefined, 1),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      pantry.water(30),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: { tr: { template: "Fırını 220°C'ye önceden ısıtın." } },
      },
      {
        id: "roast",
        template:
          "Toss {vegetable.name} and {protein.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name}. Roast for 25 minutes.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve {protein.name} ile {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} karıştırın. 25 dakika fırınlayın.",
          },
        },
      },
      {
        id: "make-dressing",
        template:
          "Whisk {dressing.name}, {acid.name}, and {water.name} until pourable.",
        translations: {
          tr: {
            template:
              "{dressing.name}, {acid.name} ve {water.name} akışkan olana kadar çırpın.",
          },
        },
      },
      {
        id: "build-bowls",
        template:
          "Layer {base.name}, {green.name}, and the roasted mixture in bowls. Spoon over the dressing.",
        translations: {
          tr: {
            template:
              "Kaselere {base.name}, {green.name} ve fırınlanmış karışımı katman katman yerleştirin. Üzerine sosu gezdirin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "bitter"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "roasted-broccoli-chickpea-bowl",
    nameTemplate: "Roasted {vegetable.name} & {protein.name} {base.name} bowl",
    translations: {
      tr: {
        nameTemplate:
          "Fırınlanmış {vegetable.name} ve {protein.name} {base.name} kasesi",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 35,
    slots: [
      slot("base", "Grain", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("vegetable", "Roasting vegetable", true, "broccoli", [
        option("broccoli", 350, "g", "cut into florets", 3),
        option("bell-pepper", 300, "g", "cut into strips", 2),
        option("eggplant", 300, "g", "cut into 2 cm cubes", 1),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained and dried", 3),
        option("black-beans", 240, "g", "drained and dried", 2),
      ]),
      slot("sauce", "Sauce", true, "peanut-butter", [
        option("peanut-butter", 3, "tbsp", undefined, 3),
        option("tahini", 3, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("green", "Fresh green", false, "spinach", [
        option("spinach", 80, "g"),
      ]),
      slot("finish", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp"),
      ]),
      pantry.water(40),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: { tr: { template: "Fırını 220°C'ye önceden ısıtın." } },
      },
      {
        id: "roast",
        template:
          "Toss {vegetable.name}, {protein.name}, and {garlic.name} with {oil.name}, {salt.name}, and {black-pepper.name}. Roast for 22 minutes until golden.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {protein.name} ve {garlic.name} ile {oil.name}, {salt.name} ve {black-pepper.name} karıştırın. Altın rengi alana kadar 22 dakika fırınlayın.",
          },
        },
      },
      {
        id: "make-sauce",
        template:
          "Whisk {sauce.name}, {acid.name}, and {water.name} into a smooth, pourable sauce.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {acid.name} ve {water.name} pürüzsüz ve akışkan bir sos olana kadar çırpın.",
          },
        },
      },
      {
        id: "build-bowls",
        template:
          "Divide {base.name} between bowls. Add {green.name} if using, then the roasted mixture. Spoon over the sauce and scatter {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} kaselere paylaştırın. Kullanıyorsanız {green.name} ekleyin, ardından fırınlanmış karışımı koyun. Sosu üzerine gezdirin ve kullanıyorsanız {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "umami"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "roasted-tomato-onion-grain-bowl",
    nameTemplate:
      "Roasted {vegetable.name} & {onion.name} grain bowl with {protein.name}",
    translations: {
      tr: {
        nameTemplate:
          "Fırınlanmış {vegetable.name} ve {onion.name} tahıl kasesi, {protein.name} ile",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 35,
    slots: [
      slot("base", "Grain base", true, "quinoa", [
        option("quinoa", 300, "g", "cooked", 3),
        option("rice", 300, "g", "cooked", 2),
      ]),
      slot("vegetable", "Roasting vegetable", true, "tomato", [
        option("tomato", 400, "g", "halved", 3),
        option("bell-pepper", 350, "g", "cut into thick strips", 1),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "cut into thin wedges"),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained and dried", 3),
        option("lentils", 200, "g", "cooked and drained", 2),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("dressing", "Creamy dressing", true, "tahini", [
        option("tahini", 2, "tbsp", undefined, 3),
        option("peanut-butter", 2, "tbsp", undefined, 2),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("cilantro", 15, "g", "roughly chopped", 2),
      ]),
      pantry.water(30),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: { tr: { template: "Fırını 220°C'ye önceden ısıtın." } },
      },
      {
        id: "roast",
        template:
          "Toss {vegetable.name}, {onion.name}, and {protein.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name} on a large baking tray. Roast for 22 minutes until caramelized.",
        translations: {
          tr: {
            template:
              "Büyük bir fırın tepsisinde {vegetable.name}, {onion.name} ve {protein.name} ile {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} karıştırın. Karamelleşene kadar 22 dakika fırınlayın.",
          },
        },
      },
      {
        id: "make-dressing",
        template:
          "Whisk {dressing.name}, {acid.name}, and {water.name} until smooth and pourable.",
        translations: {
          tr: {
            template:
              "{dressing.name}, {acid.name} ve {water.name} pürüzsüz ve akışkan olana kadar çırpın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Divide {base.name} between bowls. Spoon over the roasted mixture, drizzle with the dressing, and scatter {herb.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} kaselere paylaştırın. Üzerine fırınlanmış karışımı koyun, sosu gezdirin ve kullanıyorsanız {herb.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "sweet", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "tomato-onion-spiced-grain-bowl",
    nameTemplate: "Spiced {tomato.name} & {protein.name} grain bowl",
    translations: {
      tr: {
        nameTemplate: "Baharatlı {tomato.name} ve {protein.name} tahıl kasesi",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 25,
    slots: [
      slot("base", "Grain base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("tomato", "Sauce base", true, "tomato", [
        option("tomato", 400, "g", "crushed"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced"),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("lentils", 180, "g", "rinsed", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Fresh finish", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("cilantro", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "saute",
        template:
          "Warm {oil.name} in a frying pan over medium heat. Cook {onion.name} for 5 minutes until soft, then stir in {garlic.name} and {spice.name} for 30 seconds.",
        translations: {
          tr: {
            template:
              "Orta ateşte kızartma tavasında {oil.name} ısıtın. {onion.name} yumuşayana kadar 5 dakika pişirin, ardından {garlic.name} ve {spice.name} ekleyip 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Add {tomato.name}, {protein.name}, {salt.name}, and {black-pepper.name}. Simmer for 8 minutes until the sauce thickens.",
        translations: {
          tr: {
            template:
              "{tomato.name}, {protein.name}, {salt.name} ve {black-pepper.name} ekleyin. Sos koyulaşana kadar 8 dakika kısık ateşte pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Divide {base.name} between bowls, spoon over the tomato mixture, and add {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} kaselere paylaştırın, üzerine domates karışımını koyun ve kullanıyorsanız {finish.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "umami", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "tofu-tomato-onion-grain-bowl",
    nameTemplate: "Spiced {protein.name} & {tomato.name} bowl",
    translations: {
      tr: { nameTemplate: "Baharatlı {protein.name} ve {tomato.name} kasesi" },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 16,
    totalMinutes: 25,
    slots: [
      slot("base", "Grain", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("tomato", "Sauce", true, "tomato", [
        option("tomato", 350, "g", "diced"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "diced"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("green", "Leafy green", false, "spinach", [
        option("spinach", 80, "g", "roughly chopped", 3),
        option("cabbage", 120, "g", "finely shredded", 2),
      ]),
      slot("finish", "Fresh finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("cilantro", 15, "g", "chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "sear-tofu",
        template:
          "Heat {oil.name} in a wide pan over medium-high heat. Add {protein.name} and cook for 5 minutes until golden on two sides. Move to a plate.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada orta-yüksek ateşte {oil.name} ısıtın. {protein.name} ekleyip her iki tarafı altın rengi alana kadar 5 dakika pişirin. Bir tabağa alın.",
          },
        },
      },
      {
        id: "cook-aromatics",
        template:
          "In the same pan, cook {onion.name} over medium heat for 4 minutes. Add {garlic.name} and {spice.name}; stir for 30 seconds.",
        translations: {
          tr: {
            template:
              "Aynı tavada orta ateşte {onion.name} 4 dakika pişirin. {garlic.name} ve {spice.name} ekleyip 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "make-sauce",
        template:
          "Add {tomato.name}, {salt.name}, and {black-pepper.name}. Cook for 5 minutes until the tomatoes break down into a loose sauce.",
        translations: {
          tr: {
            template:
              "{tomato.name}, {salt.name} ve {black-pepper.name} ekleyin. Domatesler parçalanıp sos kıvamına gelene kadar 5 dakika pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Return {protein.name} and add {green.name} if using; toss for 1 minute until wilted. Divide {base.name} between bowls, spoon over the {protein.name} and sauce, and finish with {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{protein.name} geri ekleyin ve kullanıyorsanız {green.name} koyun; solana kadar 1 dakika karıştırın. {base.name} kaselere paylaştırın, üzerine {protein.name} ve sosu gezdirin, kullanıyorsanız {finish.name} ile tamamlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "umami", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "edamame-sesame-rice-bowl",
    nameTemplate: "{protein.name} & {vegetable.name} sesame {base.name} bowl",
    translations: {
      tr: {
        nameTemplate:
          "Susamlı {protein.name} ve {vegetable.name} {base.name} kasesi",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 10,
    slots: [
      slot("base", "Grain", true, "rice", [
        option("rice", 300, "g", "cooked and cooled", 3),
        option("quinoa", 300, "g", "cooked and cooled", 2),
      ]),
      slot("protein", "Protein", true, "edamame", [
        option("edamame", 200, "g", "shelled, thawed if frozen", 3),
        option("tofu", 200, "g", "firm, cubed", 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "cucumber", [
        option("cucumber", 200, "g", "thinly sliced", 3),
        option("carrot", 200, "g", "julienned or grated", 2),
        option("cabbage", 150, "g", "finely shredded", 1),
      ]),
      slot("sauce", "Dressing base", true, "tahini", [
        option("tahini", 3, "tbsp", undefined, 3),
        option("peanut-butter", 3, "tbsp", undefined, 2),
      ]),
      slot("umami", "Umami depth", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp"),
      ]),
      slot("acid", "Dressing acid", true, "lime", [
        option("lime", 1, "piece", "juiced", 3),
        option("lemon", 1, "piece", "juiced", 2),
      ]),
      slot("finish", "Garnish", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", undefined, 3),
        option("scallion", 2, "piece", "thinly sliced", 2),
        option("cilantro", 10, "g", "roughly chopped", 1),
      ]),
      pantry.salt,
    ],
    steps: [
      {
        id: "dressing",
        template:
          "Whisk together {sauce.name}, {umami.name}, and {acid.name} until smooth. Thin with 1–2 tbsp water if needed.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {umami.name} ve {acid.name} pürüzsüz olana kadar çırpın. Gerekirse 1-2 yemek kaşığı su ile kıvamını ayarlayın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Divide {base.name} between bowls. Top with {protein.name} and {vegetable.name}.",
        translations: {
          tr: {
            template:
              "{base.name} kaselere paylaştırın. Üzerine {protein.name} ve {vegetable.name} koyun.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Drizzle over the dressing and top with {finish.name} if using. Season with {salt.name} to taste.",
        translations: {
          tr: {
            template:
              "Sosu üzerine gezdirin ve kullanıyorsanız {finish.name} ekleyin. Damağınıza göre {salt.name} ile tatlandırın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "umami", "acid"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "sumac-chickpea-bulgur-bowl",
    nameTemplate: "Sumac {protein.name} & {base.name} bowl",
    translations: {
      tr: { nameTemplate: "Sumaklı {protein.name} ve {base.name} kasesi" },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 25,
    slots: [
      slot("base", "Grain", true, "bulgur", [
        option("bulgur", 160, "g", undefined, 3),
        option("couscous", 160, "g", undefined, 2),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("lentils", 240, "g", "cooked and drained", 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "tomato", [
        option("tomato", 250, "g", "finely diced", 3),
        option("cucumber", 200, "g", "finely diced", 2),
      ]),
      slot("onion", "Sharp element", true, "red-onion", [
        option("red-onion", 1, "piece", "very finely sliced", 3),
        option("scallion", 3, "piece", "thinly sliced", 2),
      ]),
      slot("spice", "Spice", true, "sumac", [
        option("sumac", 1.5, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("dressing", "Dressing acid", true, "pomegranate-molasses", [
        option("pomegranate-molasses", 1.5, "tbsp", undefined, 3),
        option("lemon", 1, "piece", "juiced", 2),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 20, "g", "finely chopped", 3),
        option("dill", 15, "g", "finely chopped", 2),
      ]),
      slot("finish", "Optional garnish", false, "pomegranate", [
        option("pomegranate", 80, "g", "seeds only", 3),
        option("pistachios", 30, "g", "roughly chopped", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      pantry.water(320),
      pantry.salt,
    ],
    steps: [
      {
        id: "cook-grain",
        template:
          "Pour 320 ml boiling {water.name} over {base.name} in a heatproof bowl. Cover and leave for 12 minutes (or follow packet instructions for {base.name}). Fluff with a fork and let cool slightly.",
        translations: {
          tr: {
            template:
              "Isıya dayanıklı bir kasenin içindeki {base.name} üzerine 320 ml kaynar {water.name} dökün. Üzerini kapatıp 12 dakika bekletin (ya da {base.name} için paket talimatlarını uygulayın). Çatalla havalandırın ve hafifçe soğumaya bırakın.",
          },
        },
      },
      {
        id: "season-protein",
        template:
          "Warm {protein.name} in a small pan over medium heat with a drizzle of {oil.name} and {spice.name}. Cook 3 minutes, stirring, until fragrant. Season with {salt.name}.",
        translations: {
          tr: {
            template:
              "Küçük bir tavada orta ateşte {protein.name}, {oil.name} ve {spice.name} ile ısıtın. Kokusu çıkana kadar karıştırarak 3 dakika pişirin. {salt.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "In a large bowl, whisk remaining {oil.name} with {dressing.name}. Add {base.name}, {vegetable.name}, {onion.name}, and {herb.name}; toss well.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede kalan {oil.name} ile {dressing.name} çırpın. {base.name}, {vegetable.name}, {onion.name} ve {herb.name} ekleyip iyice karıştırın.",
          },
        },
      },
      {
        id: "build-bowls",
        template:
          "Divide between bowls, top with the warm {protein.name}, and scatter over {finish.name} if using.",
        translations: {
          tr: {
            template:
              "Kaselere paylaştırın, üzerine sıcak {protein.name} koyun ve kullanıyorsanız {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "fat", "aroma", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "herb-vegetable-pilaf",
    nameTemplate: "{grain.name} pilaf with {vegetable.name} & {herb.name}",
    translations: {
      tr: {
        nameTemplate: "{vegetable.name} ve {herb.name} ile {grain.name} pilavı",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 3,
    activeMinutes: 15,
    totalMinutes: 35,
    slots: [
      slot("grain", "Grain", true, "rice", [
        option("rice", 200, "g", undefined, 3),
        option("bulgur", 200, "g", undefined, 2),
        option("couscous", 180, "g", undefined, 1),
      ]),
      slot("vegetable", "Vegetable", true, "carrot", [
        option("carrot", 2, "piece", "finely diced", 3),
        option("zucchini", 1, "piece", "finely diced", 2),
        option("cauliflower", 200, "g", "cut into small florets", 1),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 1, "piece", "finely diced", 3),
        option("red-onion", 1, "piece", "finely diced", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("spice", "Spice", true, "allspice", [
        option("allspice", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
        option("cinnamon", 1, "pinch", undefined, 1),
      ]),
      slot("herb", "Herb", true, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("mint", 10, "g", "roughly chopped", 2),
        option("dill", 10, "g", "roughly chopped", 1),
      ]),
      slot("nut", "Nut finish", false, "pistachios", [
        option("pistachios", 30, "g", "roughly chopped", 3),
        option("walnuts", 30, "g", "roughly chopped", 2),
        option("cashews", 30, "g", "roughly chopped", 1),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.water(400),
    ],
    steps: [
      {
        id: "saute",
        template:
          "Heat {oil.name} in a wide heavy-bottomed pot over medium heat. Cook {onion.name} for 5 minutes until golden. Add {garlic.name} and {spice.name} and stir for 1 minute until fragrant.",
        translations: {
          tr: {
            template:
              "Geniş, kalın tabanlı bir tencerede orta ateşte {oil.name} ısıtın. {onion.name} altın rengi alana kadar 5 dakika pişirin. {garlic.name} ve {spice.name} ekleyip kokusu çıkana kadar 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "toast-grain",
        template:
          "Add {grain.name} and stir to coat in the oil. Toast for 2 minutes, then add {vegetable.name} and stir together.",
        translations: {
          tr: {
            template:
              "{grain.name} ekleyip yağa bulanması için karıştırın. 2 dakika kavurun, ardından {vegetable.name} ekleyip birlikte karıştırın.",
          },
        },
      },
      {
        id: "cook",
        template:
          "Pour in {water.name} and season generously with {salt.name}. Bring to a boil, then cover tightly and reduce heat to low. Cook until the grain is tender and liquid fully absorbed (rice 18 min, bulgur 12 min, couscous 10 min).",
        translations: {
          tr: {
            template:
              "{water.name} dökün ve {salt.name} ile bol miktarda tatlandırın. Kaynamaya başlayınca kapağını sıkıca kapatıp ateşi kısın. Tahıl yumuşayıp sıvı tamamen çekilene kadar pişirin (pirinç 18 dk, bulgur 12 dk, kuskus 10 dk).",
          },
        },
      },
      {
        id: "serve",
        template:
          "Remove from heat and rest covered for 5 minutes. Fluff with a fork, scatter {herb.name} and {nut.name} if using over the top, and serve.",
        translations: {
          tr: {
            template:
              "Ocaktan alın ve kapalı şekilde 5 dakika dinlendirin. Çatalla havalandırın, üzerine {herb.name} ve kullanıyorsanız {nut.name} serpin ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "aroma", "bulk", "fat", "fresh"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "mujaddara",
    nameTemplate: "Mujaddara with crispy {onion.name}",
    translations: { tr: { nameTemplate: "Çıtır {onion.name} ile müceddere" } },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 20,
    totalMinutes: 55,
    slots: [
      slot("grain", "Grain", true, "rice", [
        option("rice", 200, "g", "rinsed", 3),
        option("bulgur", 200, "g", undefined, 2),
      ]),
      slot("lentil", "Lentils", true, "lentils", [
        option("lentils", 200, "g", "rinsed"),
      ]),
      slot("onion", "Onion", true, "onion", [
        option("onion", 2, "piece", undefined, 3),
        option("red-onion", 2, "piece", undefined, 2),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("allspice", 1, "tsp", undefined, 2),
        option("cinnamon", 1, "pinch", undefined, 1),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("lime", 1, "piece", "juiced", 2),
      ]),
      slot("herb", "Herb finish", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("mint", 10, "g", "roughly chopped", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 4, "tbsp"),
      ]),
      pantry.salt,
      pantry.water(700),
    ],
    steps: [
      {
        id: "cook-lentils",
        template:
          "Bring {water.name} to a boil in a medium pot. Add {lentil.name} and a pinch of {salt.name} and simmer 10 minutes until just tender. Drain, reserving the cooking water.",
        translations: {
          tr: {
            template:
              "Orta boy bir tencerede {water.name} kaynatın. {lentil.name} ve bir tutam {salt.name} ekleyip tam yumuşayana kadar 10 dakika hafif kaynamada pişirin. Pişirme suyunu saklayarak süzün.",
          },
        },
      },
      {
        id: "cook-grain",
        template:
          "In the same pot, heat half the {oil.name} over medium heat. Add {spice.name} and stir 30 seconds. Add {grain.name} and the par-cooked {lentil.name}, pour in enough of the reserved cooking water to cover by 2 cm, and season with {salt.name}. Bring to a boil, cover, and cook on low until the grain is tender and liquid absorbed (rice 18 min, bulgur 12 min).",
        translations: {
          tr: {
            template:
              "Aynı tencerede orta ateşte {oil.name} ısıtın. {spice.name} ekleyip 30 saniye karıştırın. {grain.name} ve yarı pişmiş {lentil.name} ekleyin, 2 cm geçecek kadar saklanan pişirme suyu dökün ve {salt.name} ile tatlandırın. Kaynayınca kapağını kapatıp kısık ateşte tahıl yumuşayıp sıvı çekilene kadar pişirin (pirinç 18 dk, bulgur 12 dk).",
          },
        },
      },
      {
        id: "crispy-onion",
        template:
          "Meanwhile, thinly slice {onion.name}. Heat remaining {oil.name} in a wide frying pan over medium-high heat. Cook {onion.name}, stirring often, for 15–18 minutes until deeply caramelised and crisp. Season with {salt.name}.",
        translations: {
          tr: {
            template:
              "Bu arada {onion.name} ince ince dilimleyin. Geniş bir tavada orta-yüksek ateşte kalan {oil.name} ısıtın. {onion.name} sık sık karıştırarak derin karamel rengini alıp çıtır olana kadar 15-18 dakika pişirin. {salt.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Fluff the grain with a fork, squeeze over {acid.name}, and mound onto a serving plate. Pile the crispy {onion.name} on top and scatter {herb.name} if using.",
        translations: {
          tr: {
            template:
              "Tahılı çatalla havalandırın, üzerine {acid.name} sıkın ve servis tabağına yığın. Üzerine çıtır {onion.name} koyun ve kullanıyorsanız {herb.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "aroma", "acid", "bulk"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "teriyaki-grain-bowl",
    nameTemplate: "Teriyaki-glazed {protein.name} & {vegetable.name} bowl",
    translations: {
      tr: {
        nameTemplate:
          "Teriyaki soslu {protein.name} ve {vegetable.name} kasesi",
      },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 30,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and cut into thick slabs", 3),
        option("tempeh", 200, "g", "cut into thick slabs", 2),
        option("edamame", 200, "g", "shelled", 1),
      ]),
      slot("grain", "Grain base", true, "rice", [
        option("rice", 180, "g", "cooked", 3),
        option("spaghetti", 140, "g", undefined, 2),
        option("couscous", 150, "g", undefined, 1),
      ]),
      slot("vegetable", "Vegetable", true, "snap-peas", [
        option("snap-peas", 150, "g", "strings removed", 3),
        option("snow-peas", 150, "g", "strings removed", 2),
        option("bell-pepper", 1, "piece", "thinly sliced", 1),
      ]),
      slot("sweet", "Glaze sweetener", true, "maple-syrup", [
        option("maple-syrup", 2, "tbsp"),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 3, "tbsp"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely grated"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely grated"),
      ]),
      slot("acid", "Acid balance", true, "rice-vinegar", [
        option("rice-vinegar", 1, "tbsp", undefined, 3),
        option("lime", 0.5, "piece", "juiced", 2),
      ]),
      slot("garnish", "Garnish", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", "lightly toasted", 3),
        option("scallion", 2, "piece", "thinly sliced", 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      slot("oil", "Cooking fat", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp"),
      ]),
    ],
    steps: [
      {
        id: "make-glaze",
        template:
          "Stir together {sweet.name}, {sauce.name}, {ginger.name}, {garlic.name}, and {acid.name} in a small bowl until combined.",
        translations: {
          tr: {
            template:
              "Küçük bir kasede {sweet.name}, {sauce.name}, {ginger.name}, {garlic.name} ve {acid.name} birleşene kadar karıştırın.",
          },
        },
      },
      {
        id: "cook-grain",
        template:
          "If using raw {grain.name}, cook it now according to packet instructions. Keep warm.",
        translations: {
          tr: {
            template:
              "Çiğ {grain.name} kullanıyorsanız paket talimatlarına göre şimdi pişirin. Sıcak tutun.",
          },
        },
      },
      {
        id: "sear-protein",
        template:
          "Heat {oil.name} in a wide non-stick pan over medium-high heat. Add {protein.name} in a single layer and sear undisturbed for 3–4 minutes until golden. Flip and cook 2 minutes more.",
        translations: {
          tr: {
            template:
              "Geniş yapışmaz bir tavada orta-yüksek ateşte {oil.name} ısıtın. {protein.name} tek kat halinde ekleyip altın rengi alana kadar dokunmadan 3-4 dakika kızartın. Çevirin ve 2 dakika daha pişirin.",
          },
        },
      },
      {
        id: "glaze",
        template:
          "Pour the glaze over the {protein.name} and add {vegetable.name} to the pan. Cook, stirring and turning everything, for 2–3 minutes until the glaze thickens and coats the protein and the vegetable is just tender.",
        translations: {
          tr: {
            template:
              "Sosu {protein.name} üzerine dökün ve tavaya {vegetable.name} ekleyin. Sos koyulaşıp proteine yapışana ve sebze tam yumuşayana kadar karıştırarak ve çevirerek 2-3 dakika pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Spoon {grain.name} into bowls, top with glazed {protein.name} and {vegetable.name}. Scatter {garnish.name} if using over the top.",
        translations: {
          tr: {
            template:
              "Kaselere {grain.name} koyun, üzerine soslu {protein.name} ve {vegetable.name} ekleyin. Kullanıyorsanız üzerine {garnish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "sweet", "umami", "starch", "crunch"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "herb-tahdig",
    nameTemplate: "Herb tahdig with {herb.name} & {allium.name}",
    translations: {
      tr: { nameTemplate: "{herb.name} ve {allium.name} ile otlu tahdig" },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 3,
    activeMinutes: 20,
    totalMinutes: 60,
    slots: [
      slot("grain", "Long-grain rice", true, "rice", [
        option(
          "rice",
          300,
          "g",
          "long-grain or basmati, rinsed 3–4 times until water runs clear",
        ),
      ]),
      slot("herb", "Fresh herb", true, "dill", [
        option("dill", 40, "g", "roughly chopped", 3),
        option("parsley", 40, "g", "roughly chopped", 2),
        option("mint", 30, "g", "roughly chopped", 1),
      ]),
      slot("allium", "Allium", true, "scallion", [
        option("scallion", 5, "piece", "thinly sliced", 3),
        option("leek", 1, "piece", "thinly sliced", 2),
      ]),
      slot("base", "Crust base (optional)", false, "potato", [
        option("potato", 200, "g", "peeled and sliced into 0.5 cm rounds"),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 4, "tbsp")]),
      pantry.salt,
      pantry.water(600),
    ],
    steps: [
      {
        id: "parboil",
        template:
          "Bring a large pot of generously salted {water.name} to a rolling boil. Add {grain.name} and cook for 6–7 minutes — the rice should be just slightly firm in the very centre (it will finish in the steaming step). Drain immediately through a fine sieve and rinse briefly with cool water to stop cooking.",
        translations: {
          tr: {
            template:
              "Büyük bir tencerede bol tuzlu {water.name} coşkulu bir şekilde kaynatın. {grain.name} ekleyip 6-7 dakika pişirin; pirincin tam ortası hafifçe sert kalmalıdır (buharda pişirme adımında tamamlanacaktır). Hemen ince bir süzgeçten geçirin ve pişmeyi durdurmak için kısaca soğuk suyla durulayın.",
          },
        },
      },
      {
        id: "mix-herbs",
        template:
          "Combine the drained {grain.name} with {herb.name} and {allium.name} in a bowl. Toss gently to distribute the herbs evenly through the rice.",
        translations: {
          tr: {
            template:
              "Süzülmüş {grain.name} ile {herb.name} ve {allium.name} kasede birleştirin. Otların pirinç içinde eşit dağılması için nazikçe karıştırın.",
          },
        },
      },
      {
        id: "build-crust",
        template:
          "Heat {oil.name} in a heavy-bottomed pot (ideally a Dutch oven or non-stick pan with a tight lid) over medium-high heat. If using {base.name}, arrange slices in a single layer over the base of the pot. Otherwise, pour the oil directly onto the pot base. Mound the herb rice in, pressing it gently into a dome shape — do not pack it tight. With the handle of a wooden spoon, poke 5–6 holes through the rice down to the base to allow steam to escape.",
        translations: {
          tr: {
            template:
              "Kalın tabanlı bir tencerede (tercihen döküm tencere veya sıkı kapağı olan yapışmaz tava) orta-yüksek ateşte {oil.name} ısıtın. Kullanıyorsanız {base.name} dilimlerini tencerenin tabanına tek kat halinde dizin. Değilse yağı doğrudan taban üzerine dökün. Otlu pirinci içine koyup hafifçe kubbe şeklinde bastırın; sıkı paketlemeyin. Tahta kaşığın sapıyla buharın çıkması için pirincin içinden tabana kadar 5-6 delik açın.",
          },
        },
      },
      {
        id: "steam",
        template:
          "Wrap the pot lid tightly in a clean tea towel — this absorbs steam and keeps the rice fluffy. Cover the pot, reduce heat to the lowest setting possible, and cook undisturbed for 35–40 minutes. The crust is done when the rice at the edges looks golden and slightly pulled away from the sides, and you hear a gentle sizzle.",
        translations: {
          tr: {
            template:
              "Tencerenin kapağını temiz bir mutfak bezine sıkıca sarın; bu buharı emer ve pirinci kabarık tutar. Tencereyi kapatın, ateşi mümkün olan en düşük seviyeye alın ve dokunmadan 35-40 dakika pişirin. Kenarlardaki pirinç altın rengi alıp kenarlardan hafifçe ayrıldığında ve hafif bir cızırtı duyduğunuzda kabuk hazırdır.",
          },
        },
      },
      {
        id: "invert",
        template:
          "Remove from heat and rest the covered pot for 5 minutes. Place a large flat plate or platter over the pot. Working quickly and confidently, invert in one motion — the tahdig should release cleanly onto the plate with the golden crust facing up. If it sticks, return to low heat for 2 minutes, then try again.",
        translations: {
          tr: {
            template:
              "Ocaktan alın ve kapalı tencereyi 5 dakika dinlendirin. Tencerenin üzerine büyük düz bir tabak veya servis tabağı koyun. Hızlı ve kararlı biçimde tek hareketle ters çevirin; tahdig altın kabuk üstte olacak şekilde tabağa temizce ayrılmalıdır. Yapışırsa kısık ateşe geri koyup 2 dakika bekleyin ve tekrar deneyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "fresh", "fat", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "jollof-rice",
    nameTemplate: "Jollof rice with {spice.name} & {allium.name}",
    translations: {
      tr: { nameTemplate: "{spice.name} ve {allium.name} ile jollof pirinç" },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 4,
    activeMinutes: 25,
    totalMinutes: 55,
    slots: [
      slot("grain", "Long-grain rice", true, "rice", [
        option(
          "rice",
          350,
          "g",
          "long-grain or parboiled, rinsed until water runs clear",
        ),
      ]),
      slot("tomato", "Tomato base", true, "tomato", [
        option("tomato", 4, "piece", "roughly chopped"),
      ]),
      slot("pepper", "Bell pepper", true, "bell-pepper", [
        option("bell-pepper", 1, "piece", "roughly chopped"),
      ]),
      slot("allium", "Allium", true, "onion", [
        option(
          "onion",
          1,
          "piece",
          "half reserved for blending, half finely diced",
          3,
        ),
        option(
          "red-onion",
          1,
          "piece",
          "half reserved for blending, half finely diced",
          2,
        ),
      ]),
      slot("spice", "Warm spice", true, "smoked-paprika", [
        option("smoked-paprika", 2, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("aromatics", "Aromatics", true, "garlic", [
        option("garlic", 4, "piece", "minced", 3),
        option("ginger", 20, "g", "minced", 2),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      pantry.salt,
      pantry.water(480),
    ],
    steps: [
      {
        id: "blend-base",
        template:
          "Roughly chop {tomato.name}, {pepper.name}, and half the {allium.name}. Blend together until smooth — a blender or immersion blender both work. The purée should be uniformly liquid with no large chunks.",
        translations: {
          tr: {
            template:
              "{tomato.name}, {pepper.name} ve {allium.name} nin yarısını kabaca doğrayın. Blender veya daldırma blender ile pürüzsüz olana kadar blendırdan geçirin. Püre büyük parçasız, tamamen sıvı kıvamında olmalıdır.",
          },
        },
      },
      {
        id: "fry-paste",
        template:
          "Heat {oil.name} in a wide, heavy pot over medium heat. Add the remaining finely diced {allium.name} and {aromatics.name}. Cook, stirring occasionally, for 6–8 minutes until the onion is soft and beginning to colour. Pour in the blended tomato purée — stand back, it will splutter. Stir in {spice.name} and season generously with {salt.name}. Cook uncovered over medium heat, stirring every few minutes, for 15–18 minutes until the paste has darkened to a deep brick red, the raw tomato smell has gone, and the oil begins to separate around the edges. This long fry-down is the foundation of jollof rice — do not rush it.",
        translations: {
          tr: {
            template:
              "Geniş, ağır bir tencerede orta ateşte {oil.name} ısıtın. Kalan ince doğranmış {allium.name} ve {aromatics.name} ekleyin. Soğan yumuşayıp rengi dönmeye başlayana kadar ara sıra karıştırarak 6-8 dakika pişirin. Blendırdan geçirilmiş domates püresini dökün; sıçrayacağından dikkatli olun. {spice.name} karıştırın ve {salt.name} ile bol miktarda tatlandırın. Orta ateşte kapaksız, birkaç dakikada bir karıştırarak, paste koyu tuğla kırmızısına dönene, çiğ domates kokusu geçene ve yağ kenarlarda ayrışmaya başlayana kadar 15-18 dakika pişirin. Bu uzun kavurma işlemi jollof pirincinın temelidir; acele etmeyin.",
          },
        },
      },
      {
        id: "add-rice",
        template:
          "Add {grain.name} to the pot and stir thoroughly so every grain is coated in the tomato paste. Pour in {water.name}. The liquid should just cover the rice by about 1 cm — add a splash more if needed. Bring to a vigorous boil, stir once, then reduce heat to the lowest possible setting and cover the pot tightly.",
        translations: {
          tr: {
            template:
              "Tencereye {grain.name} ekleyin ve her tane domates pastasıyla kaplanacak şekilde iyice karıştırın. {water.name} dökün. Sıvı pirinci yaklaşık 1 cm geçmeli; gerekirse biraz daha ekleyin. Coşkulu kaynamaya getirin, bir kez karıştırın, ardından ateşi mümkün olan en düşük seviyeye alıp tencereyi sıkıca kapatın.",
          },
        },
      },
      {
        id: "steam-finish",
        template:
          "Cook undisturbed for 25–30 minutes until the rice is tender and has absorbed all the liquid. Resist the urge to lift the lid — the steam is doing the work. After 25 minutes, press a spoon gently through the centre; if liquid pools up, cover and cook a further 5 minutes.",
        translations: {
          tr: {
            template:
              "Pirinç yumuşayıp tüm sıvıyı çekene kadar dokunmadan 25-30 dakika pişirin. Kapağı kaldırma isteğine direnin; buhar işi yapıyor. 25 dakika sonra ortaya nazikçe kaşık bastırın; sıvı birikiyorsa kapağı kapatıp 5 dakika daha pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Remove from heat and rest covered for 5 minutes. Uncover and fluff gently from the top with a fork — the bottom layer will be caramelised and slightly crispy, which is the prized part. Serve straight from the pot.",
        translations: {
          tr: {
            template:
              "Ocaktan alın ve kapalı şekilde 5 dakika dinlendirin. Kapağı açın ve üstten çatalla nazikçe havalandırın; alt tabaka karamelize ve hafif çıtır olacak, bu en değerli kısımdır. Tencereden doğrudan servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "umami", "heat", "fat", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "mushroom-orzotto",
    nameTemplate: "Mushroom orzotto with {herb.name} & {allium.name}",
    translations: {
      tr: { nameTemplate: "{herb.name} ve {allium.name} ile mantarlı orzotto" },
    },
    foodType: "grain-bowl",
    primaryCookingLocation: "stovetop",
    servings: 3,
    activeMinutes: 35,
    totalMinutes: 45,
    slots: [
      slot("grain", "Pearl barley", true, "barley", [
        option("barley", 200, "g", "pearl barley, rinsed", 3),
        option("farro", 200, "g", "semi-pearled farro, rinsed", 2),
      ]),
      slot("mushroom", "Mushroom", true, "mushroom", [
        option("mushroom", 350, "g", "sliced or roughly torn"),
      ]),
      slot("allium", "Allium", true, "leek", [
        option(
          "leek",
          2,
          "piece",
          "white and light-green part only, thinly sliced",
          3,
        ),
        option("onion", 1, "piece", "finely diced", 2),
      ]),
      slot("aromatics", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "minced"),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 20, "g", "roughly chopped", 3),
        option("dill", 20, "g", "roughly chopped", 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("rice-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 4, "tbsp")]),
      pantry.salt,
      pantry.water(700),
    ],
    steps: [
      {
        id: "fry-mushrooms",
        template:
          "Heat 2 tbsp {oil.name} in a wide, heavy pan over high heat. Add {mushroom.name} in a single layer — work in two batches if they crowd the pan. Cook undisturbed for 2–3 minutes until deeply golden on the bottom, then toss and cook a further 2 minutes. Season generously with {salt.name}, remove to a plate, and set aside.",
        translations: {
          tr: {
            template:
              "Geniş, ağır bir tavada yüksek ateşte 2 yemek kaşığı {oil.name} ısıtın. {mushroom.name} tek kat halinde ekleyin; tavayı dolduruyorlarsa iki seferde çalışın. Alt taraf derin altın rengi alana kadar dokunmadan 2-3 dakika pişirin, ardından çevirip 2 dakika daha pişirin. {salt.name} ile bol tatlandırın, tabağa alın ve bir kenara koyun.",
          },
        },
      },
      {
        id: "build-base",
        template:
          "Reduce heat to medium. Add the remaining {oil.name} to the pan. Add {allium.name} and cook, stirring occasionally, for 4–5 minutes until soft and translucent. Add {aromatics.name} and cook 1 minute more. Add {grain.name} and stir to coat every grain in the oil. Toast for 2 minutes, stirring constantly, until the grains look glossy.",
        translations: {
          tr: {
            template:
              "Ateşi orta seviyeye alın. Kalan {oil.name} tavaya ekleyin. {allium.name} ekleyip yumuşayıp saydam olana kadar ara sıra karıştırarak 4-5 dakika pişirin. {aromatics.name} ekleyip 1 dakika daha pişirin. {grain.name} ekleyin ve her tane yağa bulanacak şekilde karıştırın. Taneler parlak görünene kadar sürekli karıştırarak 2 dakika kavurun.",
          },
        },
      },
      {
        id: "add-liquid",
        template:
          "Heat {water.name} in a small saucepan until steaming — hot liquid is essential; cold liquid shocks the grain and stops the starch from releasing. Add a ladleful (about 100 ml) of hot water to the grain and stir frequently. When the liquid is almost fully absorbed, add the next ladleful. Maintain a steady, active simmer throughout.",
        translations: {
          tr: {
            template:
              "Küçük bir tencerede {water.name} buhar çıkana kadar ısıtın; sıcak sıvı şarttır, soğuk sıvı tahılı şok eder ve nişastanın salınmasını engeller. Tahılın üzerine bir kepçe (yaklaşık 100 ml) sıcak su ekleyin ve sık sık karıştırın. Sıvı neredeyse tamamen çekilince bir kepçe daha ekleyin. Boyunca düzenli ve aktif bir hafif kaynamayı koruyun.",
          },
        },
      },
      {
        id: "simmer",
        template:
          "Continue adding hot water and stirring for 28–35 minutes (barley) or 22–28 minutes (farro), until the grain is tender with a slight bite. The mixture should be loose and creamy — the starch released by the grain creates a silky sauce with no added cream. You may not use all the water, or you may need a splash more from the kettle.",
        translations: {
          tr: {
            template:
              "Tahıl hafif dirençli ama yumuşak olana kadar sıcak su eklemeye ve karıştırmaya devam edin (arpa için 28-35 dk, farro için 22-28 dk). Karışım akıcı ve kremamsı olmalıdır; tahılın saldığı nişasta ek krema kullanmadan ipeksi bir sos oluşturur. Tüm suyu kullanmayabilirsiniz ya da kettle'dan biraz daha eklemeniz gerekebilir.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Stir the reserved {mushroom.name} back into the pan along with {herb.name} and {acid.name}. Drizzle over the last of the {oil.name}. Taste and adjust seasoning. The orzotto should flow slowly when you tilt the pan — add a splash more hot water and stir briefly if it has tightened. Serve immediately.",
        translations: {
          tr: {
            template:
              "Ayrılan {mushroom.name} ile {herb.name} ve {acid.name} tavaya geri karıştırın. Kalan {oil.name} üzerine gezdirin. Tadın ve baharatı ayarlayın. Tavayı eğdiğinizde orzotto yavaşça akmalıdır; katılaştıysa biraz sıcak su ekleyip kısaca karıştırın. Hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "umami", "fat", "aroma", "acid"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
];
